import { useEffect, useState, useCallback } from 'react';
import useAppConfig from './useAppConfig';

declare global {
    interface Window {
        Safe2Pay?: any;
    }
}

type AuthStatus = 'idle' | 'processing' | 'success' | 'failure';

export type ExternalAuthenticationResult = {
    Cavv?: string;
    Xid: string;
    Eci: string;
    Version: string;
    ReferenceId: string;
};

type AuthenticationParams = {
    currency: 'BRL';
    installments: '01';
    cardnumber: string;
    cardexpirationmonth: string;
    cardexpirationyear: string;
    cardalias: string;
};

const isDevelopment = () => {
    return process.env.REACT_APP_ENV !== 'production';
};

const useSafe2Pay3DS = () => {
    const [ready, setReady] = useState(false);
    const [status, setStatus] = useState<AuthStatus>('idle');
    const [result, setResult] = useState<ExternalAuthenticationResult>();

    const { pricing } = useAppConfig();
    const rent_preauthorization_value = pricing?.rent_preauthorization_value;

    useEffect(() => {
        if (
            !window.Safe2Pay ||
            typeof window.Safe2Pay.Mpi?.addEventListener !== 'function' ||
            !rent_preauthorization_value
        ) {
            console.warn('Safe2Pay.Mpi not ready');
            return;
        }

        const handleReady = () => setReady(true);

        window.Safe2Pay.Mpi.addEventListener('onReady', handleReady);
        window.Safe2Pay.Mpi.Init(
            {
                IsEnabled: true,
                IsSandbox: isDevelopment(),
                IsDebug: isDevelopment(),
                IsChallengeSuppressed: false,
            },
            Number(rent_preauthorization_value),
        );

        return () => {
            if (typeof window.Safe2Pay?.Mpi?.removeEventListener === 'function') {
                window.Safe2Pay.Mpi.removeEventListener('onReady', handleReady);
            }
        };
    }, [rent_preauthorization_value]);

    const authenticate = useCallback(
        (
            paymentData: Omit<AuthenticationParams, 'currency' | 'installments'>,
        ): Promise<ExternalAuthenticationResult> => {
            if (!window.Safe2Pay?.Mpi) {
                return Promise.reject(new Error('Safe2Pay.Mpi is not available'));
            }

            setStatus('processing');
            setResult(undefined);

            const payload = {
                ...paymentData,
                currency: 'BRL',
                installments: '01',
            };

            return new Promise((resolve) => {
                const handleSuccess = (e: ExternalAuthenticationResult) => {
                    setStatus('success');
                    setResult(e);
                    cleanup();
                    resolve(e);
                };

                const handleFailure = (e: ExternalAuthenticationResult) => {
                    setStatus('failure');
                    setResult(e);
                    cleanup();
                    resolve(e);
                };

                const cleanup = () => {
                    if (typeof window.Safe2Pay?.Mpi?.removeEventListener === 'function') {
                        window.Safe2Pay.Mpi.removeEventListener('onSuccess', handleSuccess);
                        window.Safe2Pay.Mpi.removeEventListener('onFailure', handleFailure);
                        window.Safe2Pay.Mpi.removeEventListener('onError', handleFailure);
                    }
                };

                window.Safe2Pay.Mpi.addEventListener('onSuccess', handleSuccess);
                window.Safe2Pay.Mpi.addEventListener('onFailure', handleFailure);
                window.Safe2Pay.Mpi.addEventListener('onError', handleFailure);

                window.Safe2Pay.Mpi.Checkout(payload);
            });
        },
        [],
    );

    return { authenticate, ready, status, result };
};

export default useSafe2Pay3DS;
